import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCrossFlow } from '@atlassiansox/cross-flow-support';
import type { TouchpointSourceType } from '@trello/ad-sequencer';
import { isDesktop } from '@trello/browser';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import type { ExperimentVariations } from '@trello/feature-gate-client';
import { useGetExperimentValue } from '@trello/feature-gate-client';
import { useHasValidAaSession } from '@trello/heartbeat/session';

import { getAvailableSites } from '../getAvailableSites';
import {
  type TrelloCrossFlowExperiment,
  trelloCrossFlowOpen,
} from '../TrelloCrossFlow';
import { isSourceTargetedByFeatureKey } from './isSourceTargetedByFeatureFlag';
import { useEligibleWorkspacesForProvisioning } from './useEligibleWorkspacesForProvisioning';
const experimentFeatureKey =
  'trello_xf_free_ppm_workspaces_less_than_ten_users';

const oneTimeMessageSources: Partial<Record<TouchpointSourceType, string>> = {
  cardBackDatePicker: '1p-discovery-ad-card-date-range-picker',
  createBoardInlineDialog: '1p-discovery-ad-left-hand-nav-board-create',
  memberHomeWorkspaceSidebarSection: '1p-discovery-ad-left-hand-nav-board-home',
  boardDirectory: '1p-discovery-ad-powerup-directory',
};

export type CrossFlowForPpmFreeWorkspacesLessThanTenUsersExperimentVariations =
  ExperimentVariations<typeof experimentFeatureKey, 'cohort'>;

export interface CrossFlowForPpmFreeWorkspacesLessThanTenUsersExperimentParams {
  source: TouchpointSourceType;
  workspaceId?: string;
  boardId?: string;
}

export const useCrossFlowForPpmFreeWorkspacesLessThanTenUsersExperiment = ({
  source,
  workspaceId,
  boardId,
}: CrossFlowForPpmFreeWorkspacesLessThanTenUsersExperimentParams): TrelloCrossFlowExperiment<CrossFlowForPpmFreeWorkspacesLessThanTenUsersExperimentVariations> => {
  const crossFlow = useCrossFlow();

  const [hasValidAaSession] = useHasValidAaSession();
  const [loadingAvailableSites, setLoadingAvailableSites] = useState(false);

  const [fireExposureEvent, setFireExposureEvent] = useState(false);

  const { cohort } = useGetExperimentValue({
    experimentName: experimentFeatureKey,
    parameter: 'cohort',
    fireExposureEvent,
  });

  const eligibleWorkspaceOptions = useEligibleWorkspacesForProvisioning({
    workspaceId,
    entitlementRequired: 'free',
  });

  const [siteEligibility, setSiteEligibility] = useState(false);

  const { isOneTimeMessageDismissed } = useOneTimeMessagesDismissed();

  const isSourceOneTimeMessageDismissed = useCallback(
    () => isOneTimeMessageDismissed(oneTimeMessageSources?.[source] || ''),
    [source, isOneTimeMessageDismissed],
  );

  const isRuntimeEligibilityChecksPassed = useMemo(() => {
    return (
      !isDesktop() &&
      siteEligibility &&
      eligibleWorkspaceOptions.length > 0 &&
      cohort !== 'not-enrolled' &&
      isSourceTargetedByFeatureKey({
        featureKey: experimentFeatureKey,
        source,
      }) &&
      !isSourceOneTimeMessageDismissed()
    );
  }, [
    siteEligibility,
    eligibleWorkspaceOptions.length,
    cohort,
    source,
    isSourceOneTimeMessageDismissed,
  ]);

  const getIsTargeted = useCallback(() => {
    return cohort !== 'not-enrolled';
  }, [cohort]);

  const getExperimentCohort = useCallback(() => {
    if (isRuntimeEligibilityChecksPassed) {
      setFireExposureEvent(true);
      return cohort;
    }
    return 'not-enrolled';
  }, [cohort, isRuntimeEligibilityChecksPassed]);

  useEffect(() => {
    const loadSiteEligibility = async () => {
      try {
        setLoadingAvailableSites(true);
        const { sites } = await getAvailableSites();
        const hasNoSites =
          sites?.length > 0 &&
          sites?.filter(({ cloudId }) => cloudId !== 'TRELLO').length === 0;
        setSiteEligibility(hasNoSites);
      } finally {
        setLoadingAvailableSites(false);
      }
    };

    if (
      !loadingAvailableSites &&
      crossFlow.isEnabled &&
      !isDesktop() &&
      isSourceTargetedByFeatureKey({
        featureKey: experimentFeatureKey,
        source,
      }) &&
      getIsTargeted()
    ) {
      loadSiteEligibility();
    }
    // Intentionally ignoring loadingAvailableSites dependency
    // eslint-disable-next-line @trello/exhaustive-deps
  }, [
    source,
    workspaceId,
    crossFlow.isEnabled,
    experimentFeatureKey,
    getIsTargeted,
    cohort,
  ]);

  return useMemo(
    () => ({
      isEnrolled: isRuntimeEligibilityChecksPassed,
      experimentName: experimentFeatureKey,
      getIsTargeted,
      getExperimentCohort,
      open: trelloCrossFlowOpen(
        crossFlow,
        source,
        eligibleWorkspaceOptions,
        hasValidAaSession,
        {
          workspaceId,
          boardId,
        },
      ),
    }),
    [
      boardId,
      crossFlow,
      eligibleWorkspaceOptions,
      getExperimentCohort,
      getIsTargeted,
      hasValidAaSession,
      isRuntimeEligibilityChecksPassed,
      source,
      workspaceId,
    ],
  );
};
